import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Form, Modal } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'
import { useTaskRuntime } from '../contexts/TaskRuntimeContext'
import {
  buildMigrationBackup, commitMigrationRestore, MAX_BACKUP_BYTES,
  parseMigrationBackup, summarizeMigrationBackup,
} from '../utils/migrationBackup.js'

export default function MigrationBackup() {
  const inputRef = useRef(null)
  const busyRef = useRef(false)
  const [includePasswords, setIncludePasswords] = useState(false)
  const [snapshot, setSnapshot] = useState(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(() => {
    let restored = false
    try { restored = Boolean(sessionStorage.getItem('migration-restored')) } catch { /* 存储权限受限 */ }
    return restored ? { variant: 'success', text: '迁移恢复完成。请补填密码、手动登录并核对配置；即时任务不会自动执行，定时计划按下一次未来时间自动恢复调度（代理暂停时仍保持暂停）。每日次数和购买记录未迁移，今天再次执行消耗类任务前请核对游戏状态。' } : null
  })
  useEffect(() => {
    try { sessionStorage.removeItem('migration-restored') } catch { /* 存储权限受限 */ }
  }, [])
  const { cancelAllScheduledTimers } = useTaskRuntime()
  const summary = snapshot ? summarizeMigrationBackup(snapshot) : null

  const download = () => {
    try {
      const backup = buildMigrationBackup(useAppStore.getState(), { includePasswords })
      const content = JSON.stringify(backup, null, 2)
      if (new TextEncoder().encode(content).length > MAX_BACKUP_BYTES) throw new Error('备份文件超过 20 MB')
      const url = URL.createObjectURL(new Blob([content], { type: 'application/json;charset=utf-8' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `hanjiang-helper-migration-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setMessage({ variant: 'success', text: '迁移备份已下载，请妥善保存；备份只保存在本机，没有上传服务器。' })
    } catch (error) {
      setMessage({ variant: 'danger', text: error.message })
    }
  }

  const readFile = async event => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setMessage(null)
    try {
      if (file.size > MAX_BACKUP_BYTES) throw new Error('备份文件超过 20 MB')
      setSnapshot(parseMigrationBackup(await file.text()))
    } catch (error) {
      setSnapshot(null)
      setMessage({ variant: 'danger', text: error.message })
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  const restore = () => {
    if (!snapshot || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    try {
      // 写存储之前先确认能读取旧数据，读取被禁用时不停止现有执行。
      localStorage.getItem('hanjiang-helper-storage')
      const manager = useAppStore.getState().taskManager
      if (manager) {
        manager.pauseForMigration()
      }
      useAppStore.getState().apiService?.abortActiveRequests()
      cancelAllScheduledTimers()
      commitMigrationRestore({
        snapshot, getState: useAppStore.getState, setState: useAppStore.setState, storage: localStorage,
      })
    } catch (error) {
      busyRef.current = false
      setBusy(false)
      setMessage({ variant: 'danger', text: `${error.message}。请备份原数据后刷新页面再试。` })
      setSnapshot(null)
      return
    }
    // 成功提示不是恢复事务的一部分，sessionStorage 不可用时也应正常刷新。
    try { sessionStorage.setItem('migration-restored', '1') } catch { /* 不影响数据恢复 */ }
    window.location.reload()
  }

  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">迁移备份 / 恢复</h5></Card.Header>
      <Card.Body>
        <p className="text-muted">更换网址或浏览器前，备份账号标识、配置、任务组、全部定时任务配置和长期学习数据（答题题库、小黑屋及劫掠目标）。不包含登录会话、每日记录或访问口令。</p>
        <Form.Check id="migration-include-passwords" label="包含账号密码（默认不包含）" checked={includePasswords}
          onChange={event => setIncludePasswords(event.target.checked)} disabled={busy} />
        {includePasswords && <Alert variant="warning" className="mt-2">备份将包含明文账号密码，请仅保存在自己的设备上，不要上传或分享。</Alert>}
        <div className="d-flex gap-2 flex-wrap mt-3">
          <Button onClick={download} disabled={busy}>迁移备份</Button>
          <Button variant="outline-primary" onClick={() => inputRef.current?.click()} disabled={busy}>
            {busy ? '处理中...' : '恢复备份'}
          </Button>
          <input ref={inputRef} type="file" accept=".json,application/json" aria-label="选择迁移备份文件"
            hidden onChange={readFile} disabled={busy} />
        </div>
        {message && <Alert variant={message.variant} className="mt-3 mb-0" role="status">{message.text}</Alert>}
      </Card.Body>
      <Modal show={Boolean(snapshot)} onHide={() => !busy && setSnapshot(null)} backdrop="static" keyboard={!busy}>
        <Modal.Header closeButton={!busy}><Modal.Title>确认覆盖恢复</Modal.Title></Modal.Header>
        <Modal.Body>
          {summary && <p>账号 {summary.accounts} 个，定时计划 {summary.scheduledJobs} 个，题库 {summary.answers} 条，成功目标 {summary.successfulTargets} 个，黑名单 {summary.blacklistedTargets} 个。</p>}
          <p>密码：{snapshot?.includesPasswords ? '文件包含密码' : '不包含，请恢复后补填'}。</p>
          <Alert variant="warning">将覆盖当前账号、配置和学习数据，停止当前运行并覆盖定时计划。建议先下载当前数据备份，并关闭此网址的其他标签页，避免旧页面覆盖数据。已发送到游戏服务器的操作无法撤回。</Alert>
          <p className="mb-0">恢复后请补填密码并手动登录；定时计划将按下一次未来时间自动恢复调度，不补跑过期操作，代理暂停时不会调度。每日次数和购买记录未迁移，请核对游戏状态再执行。</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={download} disabled={busy}>备份当前数据</Button>
          <Button variant="secondary" onClick={() => setSnapshot(null)} disabled={busy}>取消</Button>
          <Button variant="danger" onClick={restore} disabled={busy}>确认覆盖并恢复</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}
